/**
 * The Landed card (core landed.ts; docs/ux/PEEK-AND-RESUME.md §4.1): the toast's slot, grown. What was saved → where it
 * went, and who acts on it. NOT a modal: no scrim, no focus trap, focus never moves to it.
 *  - Drains for 3 s (30 s when it offers Undo). A held finger, hover or focus stops the drain; letting go carries on.
 *  - Pass-through: a tap anywhere else lands on what's under it AND drops the card (0 extra taps for the next item).
 *    A second save to the same place within its time merges into it ("2 on the order list"), never past 10 s from the first.
 *  - Tap the card: the peek (components/Peek.tsx) over the screen you're on. Undo, on the right, undoes the same record as
 *    the on-page Undo; a second undo says "Already undone".
 *  - Reduce Motion: no rise or drop, a static bar, gone at 3 s (or 10 s).
 * Moment: the owner at the 7:30 rush taps "Low" on toilet paper with a portafilter in hand; the card answers "where" with no
 * tap, so the drink wins (spec §9).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { LANDED_HELD_LINE, landedView, type LandedCard, type LandedViewer } from "@logwave/core";
import { Icon } from "./Icons.tsx";
import { useToast, type LandedOpts } from "./ui.tsx";

const undone = new Set<string>();

export function LandedCardView({ card, viewer, opts, onDrop, onPeek }: { card: LandedCard; viewer: LandedViewer; opts: Map<string, LandedOpts>; onDrop: (stashForMerge: boolean) => void; onPeek: (where: string, back: string) => void }) {
  const toast = useToast();
  const ref = useRef<HTMLDivElement>(null);
  const view = landedView(card.events, viewer);
  const last = card.events[card.events.length - 1]!;
  const o = opts.get(last.id) ?? {};
  const undo = view.undo ? o.undo ?? [...card.events].reverse().map((e) => opts.get(e.id)?.undo).find(Boolean) : undefined;
  // The drop time, moved by a hold (paused) and by a merge (the card prop's endsAt).
  const [endsAt, setEndsAt] = useState(card.endsAt);
  useEffect(() => { setEndsAt(card.endsAt); }, [card.endsAt]);
  const [held, setHeld] = useState<{ left: number; press: boolean } | null>(null);
  const hold = useCallback((press: boolean) => setHeld((h) => h ? { ...h, press: h.press || press } : { left: Math.max(0, endsAt - Date.now()), press }), [endsAt]);
  const release = useCallback(() => setHeld((h) => { if (h) setEndsAt(Date.now() + h.left); return null; }), []);
  // Time's up (not while held).
  useEffect(() => {
    if (held) return;
    const t = setTimeout(() => onDrop(false), Math.max(0, endsAt - Date.now()));
    return () => clearTimeout(t);
  }, [held, endsAt, onDrop]);
  // Never over the next control (spec §4.1 [data-next]): if the slot above the tab bar would cover one, the card goes to the top.
  useLayoutEffect(() => {
    const slot = ref.current?.parentElement;
    if (!slot || !ref.current) return;
    const hits = (r: DOMRect) => [...document.querySelectorAll("[data-next]")].some((n) => {
      if (ref.current!.contains(n)) return false;
      const b = n.getBoundingClientRect();
      return b.width > 0 && b.height > 0 && r.left < b.right && b.left < r.right && r.top < b.bottom && b.top < r.bottom;
    });
    slot.removeAttribute("data-place");
    if (hits(ref.current.getBoundingClientRect())) {
      slot.setAttribute("data-place", "top");
      if (hits(ref.current.getBoundingClientRect())) slot.removeAttribute("data-place"); // covered either way: the bottom slot, as always
    }
  }, [card.firstAt]);
  // A tap anywhere else goes through to what's under it, and drops the card (kept for a merge until its time is up).
  // A layout effect: listening from the moment the card is on screen, before any next tap can land.
  useLayoutEffect(() => {
    const down = (e: PointerEvent | MouseEvent) => { if (ref.current && e.target instanceof Node && !ref.current.contains(e.target)) onDrop(true); };
    document.addEventListener("pointerdown", down, true);
    return () => document.removeEventListener("pointerdown", down, true);
  }, [onDrop]);

  const total = Math.max(1, endsAt - card.firstAt);
  const elapsed = Math.max(0, Math.min(total, (held ? endsAt - held.left : Date.now()) - card.firstAt));
  const lines = (
    <span className="landed-lines">
      <strong className="landed-l1">{view.line1}</strong>
      <span className="landed-l2">{held?.press ? LANDED_HELD_LINE : view.line2}</span>
    </span>
  );
  const pic = <span className="landed-pic" aria-hidden="true"><Icon name={view.picture} size={24} /></span>;
  return (
    <div ref={ref} className={`landed${held ? " is-held" : ""}`} data-testid="landed" data-count={view.count} data-ms={view.ms}
      onPointerDown={() => hold(true)} onPointerUp={release} onPointerCancel={release}
      // A mouse that moves onto the card holds it (hover); one that was already resting where it appeared does not.
      onPointerMove={(e) => { if (e.pointerType === "mouse" && !held) hold(false); }} onPointerLeave={() => { if (held) release(); }}
      onFocus={() => hold(false)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) release(); }}>
      <div className="landed-drain" aria-hidden="true">
        <span key={`${endsAt}-${card.firstAt}`} style={{ animationDuration: `${total}ms`, animationDelay: `-${elapsed}ms`, animationPlayState: held ? "paused" : "running" }} />
      </div>
      {/* Line 1 and line 2 are all a screen reader hears (spec: role status, polite). */}
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">{`${view.line1}. ${view.line2}.`}</span>
      {view.peek ? (
        <button type="button" className="landed-main" aria-label={`${view.line1}. ${view.line2}. Peek at the ${view.line1.split(" → ")[1] ?? "list"}`}
          onClick={() => onPeek(o.where ?? "this screen", o.back ?? o.where ?? "this screen")}>
          {pic}{lines}<Icon name="chevron" size={20} className="landed-more" />
        </button>
      ) : <div className="landed-main">{pic}{lines}</div>}
      {undo && (
        <button type="button" className="landed-undo" onPointerDown={(e) => e.stopPropagation()} onClick={() => {
          if (undone.has(last.id)) { toast.show("Already undone.", { icon: "undo" }); onDrop(false); return; }
          undone.add(last.id);
          void undo();
          onDrop(false);
        }}>Undo</button>
      )}
    </div>
  );
}
